import React, { useCallback, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ReactComponent as PlayIcon } from "../../assets/icons/PlayIcon.svg";
import { ReactComponent as Truck } from "../../assets/icons/Truck.svg";
import { routePlanningService } from "../../services/planRoute/route-planning-service";
import type { AiRouteSuggestionDto } from "../../types/api/planRoute/routePlanning.types";
import styles from "./Planning.module.css";
import useAppSnackbar from "../../hooks/useAppSnackbar";
import { ROUTES } from "../../constants/routes";
import { savePlannedMapContext } from "../../utils/plannedMapContextStorage";
import type { MapRouteLocationState } from "../../types/navigation/mapRouteState";

function readApiError(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        if (typeof data === "string" && data.trim()) return data;
        if (data && typeof data === "object" && "message" in data) {
            const m = (data as { message?: string }).message;
            if (typeof m === "string") return m;
        }
        if (data && typeof data === "object" && "title" in data) {
            const t = (data as { title?: string }).title;
            if (typeof t === "string") return t;
        }
        return err.message || "Запрос не выполнен";
    }
    return "Неизвестная ошибка";
}

const Planning: React.FC = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    });

    const { showSuccess, SnackbarComponent } = useAppSnackbar();

    const [loading, setLoading] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [suggestions, setSuggestions] = useState<AiRouteSuggestionDto[]>([]);
    const [generateMessage, setGenerateMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [approvingDisplayId, setApprovingDisplayId] = useState<string | null>(null);

    // const availableBuses = 3;

    const fetchSuggestions = useCallback(async () => {
        setLoadingSuggestions(true);
        setError(null);
        try {
            const res = await routePlanningService.getSuggestions(date);
            setSuggestions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(readApiError(err));
            setSuggestions([]);
        } finally {
            setLoadingSuggestions(false);
        }
    }, [date]);

    const handleRegenerate = async () => {
        setLoading(true);
        setShowResults(true);
        setError(null);
        setGenerateMessage(null);
        console.log("Regenerate")
        try {
            const res = await routePlanningService.regenerate({ routeDate: date });
            setGenerateMessage(res.data?.message ?? null);
            await fetchSuggestions();
        } catch (err) {
            setError(readApiError(err));
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setLoading(true);
        setShowResults(true);
        setError(null);
        setGenerateMessage(null);

        try {
            const res = await routePlanningService.generate({ routeDate: date });
            setGenerateMessage(res.data?.message ?? null);
            await fetchSuggestions();
        } catch (err) {
            setError(readApiError(err));
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (suggestion: AiRouteSuggestionDto) => {
        setApprovingDisplayId(suggestion.displayId);
        setError(null);
        try {
            await routePlanningService.approve({
                routeDate: date,
                algorithm: suggestion.algorithm,
            });
            // Сбрасываем режим «просмотр варианта», чтобы на карте шёл обычный запрос (сегодня / логика сервера).
            savePlannedMapContext({ planningPreview: null });
            setShowResults(false);
            showSuccess("Маршрут успешно утверждён");
        } catch (err) {
            setError(readApiError(err));
        } finally {
            setApprovingDisplayId(null);
        }
    };

    const handleManualEdit = (suggestion: AiRouteSuggestionDto) => {
        const state: MapRouteLocationState = {
            planningPreview: {
                routeDate: date,
                algorithm: suggestion.algorithm?.trim() ?? "",
            },
        };
        navigate(ROUTES.MAP.path, { state });
    };

    const handleReset = () => {
        setShowResults(false);
        setSuggestions([]);
        setGenerateMessage(null);
        setError(null);
    };

    const formatScore = (pct: number | null) => {
        if (pct == null) return null;
        return `AI Score: ${pct.toFixed(1)}%`;
    };

    // Определяем карточку с наибольшим AI Score для выделения
    const bestDisplayId =
        suggestions.length > 0
            ? suggestions.reduce((best, s) =>
                    (s.aiScorePercent ?? 0) > (best.aiScorePercent ?? 0) ? s : best,
                suggestions[0]
            ).displayId
            : null;

    return (
        <div>
            <div className={styles.header}>
                <h1 className={styles.title}>Планирование маршрутов (AI)</h1>
                <p className={styles.subtitle}>
                    Автоматическое построение маршрутов на основе прогноза заполняемости
                    (GNN + Transformer)
                </p>
            </div>

            <div className={styles.container}>
                <div className={styles.formGroup}>
                    <div className={styles.inputsContainer}>
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>Дата маршрута</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className={styles.dateInput}
                            />
                        </div>
                    </div>

                    <button
                        className={styles.generateButton}
                        onClick={handleGenerate}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className={styles.spinner} />
                                Отправка запроса…
                            </>
                        ) : (
                            <>
                                <PlayIcon className={styles.icon} />
                                Сгенерировать маршруты
                            </>
                        )}
                    </button>
                </div>
            </div>

            {showResults && (
                <div className={styles.resultsContainer}>
                    <div className={styles.resultsHeader}>
                        <div className={styles.resultsTitle}>
                            <h2>Предложенные варианты</h2>
                        </div>

                        <div className={styles.resultsActions}>
                            <button
                                type="button"
                                className={styles.refresh}
                                onClick={() => void fetchSuggestions()}
                                disabled={loadingSuggestions}
                            >
                                {loadingSuggestions ? "Загрузка…" : "Обновить список"}
                            </button>
                            <button
                                type="button"
                                className={styles.refresh}
                                onClick={handleRegenerate}
                                disabled={loadingSuggestions}
                            >
                                {loadingSuggestions ? "Загрузка…" : "Перегенерировать"}
                            </button>
                            <button type="button" className={styles.reset} onClick={handleReset}>
                                Сбросить
                            </button>
                        </div>
                    </div>

                    {error && <div className={styles.errorBanner}>{error}</div>}

                    {generateMessage && (
                        <div className={styles.infoBanner}>{generateMessage}</div>
                    )}

                    {loadingSuggestions && suggestions.length === 0 && !error && (
                        <p className={styles.emptyHint}>Загрузка предложений…</p>
                    )}

                    <div className={styles.cards}>
                        {suggestions.map((s) => {
                            const isBest = s.displayId === bestDisplayId;

                            return (
                                <div
                                    key={s.displayId}
                                    className={`${styles.card} ${isBest ? styles.cardBest : ""}`}
                                >
                                    <div className={styles.cardHeader}>
                                        <div className={styles.cardTitleWrapper}>
                                            <div className={styles.truckIconWrapper}>
                                                <Truck className={styles.truckIcon} />
                                            </div>
                                            <div className={styles.cardTitleBlock}>
                                                <span className={styles.cardTitle}>{s.title}</span>
                                                {isBest && (
                                                    <span className={styles.bestLabel}>
                                                        Лучший вариант
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {formatScore(s.aiScorePercent) && (
                                            <span className={styles.score}>
                                                {formatScore(s.aiScorePercent)}
                                            </span>
                                        )}
                                    </div>

                                    <div className={styles.cardBody}>
                                        <p className={styles.routeId}>ID: {s.displayId}</p>

                                        <div className={styles.collectionPointsSection}>
                                            <p className={styles.collectionPointsTitle}>
                                                Маршрутов (ТС): {s.routesCount} · точек сбора всего:{" "}
                                                {s.totalCollectionPointsCount}
                                            </p>

                                            <p className={styles.description}>
                                                {s.optimizationNote}
                                            </p>

                                            <div className={styles.tags}>
                                                {s.algorithm ? (
                                                    <span className={styles.tag}>{s.algorithm}</span>
                                                ) : (
                                                    <span className={styles.tagMuted}>
                                                        Алгоритм не задан
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.cardActions}>
                                        <button
                                            type="button"
                                            className={styles.confirm}
                                            disabled={approvingDisplayId === s.displayId}
                                            onClick={() => void handleApprove(s)}
                                        >
                                            {approvingDisplayId === s.displayId ? "…" : "✓ Утвердить"}
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.edit}
                                            onClick={() => handleManualEdit(s)}
                                        >
                                            Изменить вручную
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <SnackbarComponent />
        </div>
    );
};

export default Planning;